import { useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { forwardRef, memo, useState } from 'react';

import Text from 'shopper/components/Text';

import TopContributorsRanking from 'components/TopContributorsRanking';
import TopContributorsRankingUser from 'components/TopContributorsRanking/TopContributorsRankingUser';

import { toFormattedOfferCount } from 'lib/offer';
import placeholder from 'lib/placeholder';
import { topContributorRankingQueryKeys } from 'lib/queryKeys';

import { getOfferTopContributorsList } from 'services/offer';

const INITIAL_DAYS_RANGE = 7;
const TOP_CONTRIBUTORS_LIMIT = 5;

const TopContributorsOffersRanking = forwardRef(
  ({ gaEventCategory = null, ...rest }, ref) => {
    const [daysRange, setDaysRange] = useState(INITIAL_DAYS_RANGE);

    const { data, isSuccess, isLoading, isFetching } = useQuery({
      queryKey: topContributorRankingQueryKeys.byOffers({ daysRange }),
      queryFn: ({ signal }) =>
        getOfferTopContributorsList(
          {
            days: daysRange,
            limit: TOP_CONTRIBUTORS_LIMIT,
          },
          { signal }
        ),
      cacheTime: 0.5 * 60 * 1000,
    });

    return (
      <TopContributorsRanking
        ref={ref}
        daysRange={daysRange}
        isLoading={isLoading || isFetching}
        title={placeholder('titles.topOffersContributors')}
        onChangeDaysRange={setDaysRange}
        {...rest}
      >
        {isSuccess && data?.topContributors.length > 0 ? (
          data?.topContributors.map((contributor, index) => (
            <TopContributorsRankingUser
              key={contributor.userId}
              count={toFormattedOfferCount(contributor.offerCount)}
              gaEventCategory={gaEventCategory}
              userPlacing={index + 1}
              {...contributor}
            />
          ))
        ) : (
          <Text className="py-4 text-center" size="size2">
            {placeholder('empty.offers')}
          </Text>
        )}
      </TopContributorsRanking>
    );
  }
);

TopContributorsOffersRanking.displayName = 'TopContributorsOffersRanking';

TopContributorsOffersRanking.propTypes = {
  gaEventCategory: PropTypes.string,
};

export default memo(TopContributorsOffersRanking);
